import { FunctionComponent, useEffect, useMemo, useState } from "react";
import {
    ActivityInterface,
    Preference,
} from "../../../interfaces/activity.interface";
import {
    OMRPOrganizationUnit,
    OMRPResource,
    OrgUnitMembership,
    ResourceFilter,
    ResourceInterface,
    ResourceProperty,
    ResourcePropertyWithValues,
} from "../../../interfaces/resource.interface";
import { Accordion, AccordionSummary } from "@material-ui/core";
import { ResourceTable } from "../resource-table/resource-table";
import { Icon } from "@o4c/plugin-components";
import _ from "lodash";
import { DayPart, TeamScheduleDate } from "../../../interfaces/day.interface";

import styles from "./resource-type-accordion.module.scss";
import { useTranslation } from "react-i18next";
import { OMRP_DISPLAY_NAME_PROPERTY_ID, OMRP_ORG_UNIT_PROPERTY_ID } from "../../views/team-schedule/team-schedule";

interface IResourceAccordionProps {
    resourceType: Preference;
    resourcesWithActivities: ResourceInterface[];
    resources: OMRPResource[];
    organizationUnits: OMRPOrganizationUnit[];
    dayParts: DayPart[];
    dates: TeamScheduleDate[];
    isFiltersDialogOpen: boolean;
    filters?: ResourceFilter;
    isSearchActive?: boolean;
    handleClickTile: (activity: ActivityInterface) => void;
}

export const ResourceTypeAccordion: FunctionComponent<
    IResourceAccordionProps
> = (props: IResourceAccordionProps) => {
    const {
        resourceType,
        resources,
        organizationUnits,
        resourcesWithActivities,
        handleClickTile,
        dayParts,
        dates,
        isFiltersDialogOpen,
        filters,
        isSearchActive,
    } = props;

    const { t } = useTranslation();

    const [isOpen, setIsOpen] = useState<boolean>(false);

    const [hasFilters, filterValues] = useMemo((): [boolean, Preference[]] => {
        if (!filters) {
            return [false, []];
        }

        const filterValues = filters?.actives.filter(
            (f: Preference) => f.isActive
        );
        return [!_.isEmpty(filterValues), filterValues];
    }, [filters]);

    const filteredResources = useMemo((): ResourceInterface[] => {
        if (!hasFilters || !filters) {
            return resourcesWithActivities;
        }

        const filtered = filterResources(
            resourcesWithActivities,
            resources,
            organizationUnits,
            filterValues,
            filters
        );

        return sortByFilterOrder(filtered, resources, organizationUnits, filters);
    }, [hasFilters, filters, resourcesWithActivities, resources, filterValues, organizationUnits]);

    const hasResources = useMemo((): boolean => {
        return !_.isEmpty(filteredResources);
    }, [filteredResources]);

    useEffect(() => {
        if (isFiltersDialogOpen) {
            setIsOpen(false);
        }
    }, [isFiltersDialogOpen]);

    useEffect(() => {
        if (isSearchActive && hasResources) {
            setIsOpen(true);
        }
        if (!isSearchActive) {
            setIsOpen(false);
        }
    }, [isSearchActive, hasResources]);

    //Creating this to provide an empty table for styling purposes
    //might not be the best solution
    const emptyResource = {
        resource: "",
        resourceId: -1,
        resourceTypeIds: [],
        dayParts: dayParts.map((d) => ({
            ...d,
            days: dates,
        })),
    };

    return (
        <>
            <tr className="accordion-row">
                <Accordion
                    expanded={
                        isFiltersDialogOpen || !hasResources ? false : isOpen
                    }
                    square={true}
                >
                    <AccordionSummary
                        aria-controls={`${resourceType.id}-content`}
                        id={`${resourceType.id}-header`}
                        onClick={() => {
                            setIsOpen(!isOpen);
                        }}
                        disabled={!hasResources}
                    >
                        <div className={styles.accordionTitle}>
                            <div className={styles.titleLabel}>
                                <div className={styles.expandIcon}>
                                    {isOpen ? (
                                        <Icon name="arrow-up" color="primary" />
                                    ) : (
                                        <Icon
                                            name="arrow-down"
                                            color="primary"
                                        />
                                    )}
                                </div>
                                <div className={styles.label}>
                                    {resourceType.id}
                                </div>
                            </div>
                            <div className={styles.filterButton}>
                                <div className={styles.filter}>
                                    {(hasFilters || isSearchActive) && (
                                        <div className={styles.filterSelected}>
                                            {`${t("resources.filtered")}`}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </AccordionSummary>
                    {hasResources ? (
                        filteredResources.map(
                            (resource: ResourceInterface, index: number) => (
                                <ResourceTable
                                    key={"resourceTable" + index}
                                    resource={resource}
                                    handleClickTile={handleClickTile}
                                />
                            )
                        )
                    ) : (
                        <ResourceTable
                            resource={emptyResource}
                            handleClickTile={handleClickTile}
                        />
                    )}
                </Accordion>
            </tr>
        </>
    );
};

//--------------Helper functions-------------------------------------------------------------

const getValuesFromResourceProperties = (
    property: ResourcePropertyWithValues,
    resourceProperties: ResourceProperty[]
) => {
    const valuesForProperty = _.filter(resourceProperties, (rp) => {
        return rp.resourcePropertyId === property.resourcePropertyId;
    });

    return _.map(valuesForProperty, (rp) => rp.value);
};

const getValuesFromOrganizationUnit = (
    organizationUnitMemberships: OrgUnitMembership[],
    organizationUnits: OMRPOrganizationUnit[],
) => {
    let values: string[] = [];
    organizationUnitMemberships.forEach(oum => {
        const organizationUnit = organizationUnits.find(ou => ou.id === oum.organizationUnitId)
        if(organizationUnit) {
            values.push(organizationUnit.displayName);
        }
    })

    return values
};

const getResourceValuesForProperty = (
    resource: ResourceInterface,
    resources: OMRPResource[],
    organizationUnits: OMRPOrganizationUnit[],
    filters: ResourceFilter
): string[] => {
    const resourceObject = resources.find((ro) => ro.id === resource.resourceId);
    if (!resourceObject) {
        return [];
    }

    if (filters.selectedProperty.resourcePropertyId === OMRP_DISPLAY_NAME_PROPERTY_ID) {
        return [resourceObject.displayName];
    }

    if (filters.selectedProperty.resourcePropertyId === OMRP_ORG_UNIT_PROPERTY_ID) {
        return getValuesFromOrganizationUnit(
            resourceObject.organizationUnitMemberships,
            organizationUnits
        );
    }

    return getValuesFromResourceProperties(
        filters.selectedProperty,
        resourceObject.resourceProperties
    );
};

const filterResources = (
    resourcesWithActivities: ResourceInterface[],
    resources: OMRPResource[],
    organizationUnits: OMRPOrganizationUnit[],
    filterValues: Preference[],
    filters: ResourceFilter
): ResourceInterface[] => {
    const activeValues = new Set(filterValues.map((p) => p.id));

    return resourcesWithActivities.filter((r) => {
        const values = getResourceValuesForProperty(r, resources, organizationUnits, filters);
        return values.some((v) => activeValues.has(v));
    });
};

const sortByFilterOrder = (
    filtered: ResourceInterface[],
    resources: OMRPResource[],
    organizationUnits: OMRPOrganizationUnit[],
    filters: ResourceFilter
): ResourceInterface[] => {
    const activeOrderMap = new Map(
        filters.actives
            .filter((a) => a.isActive)
            .map((a, index) => [a.id, index])
    );

    const sortIndexMap = new Map(
        filtered.map((r) => {
            const values = getResourceValuesForProperty(r, resources, organizationUnits, filters);
            // Use the best matching value (lowest sort position) instead of just the first
            let bestIndex = -1;
            for (const v of values) {
                const idx = activeOrderMap.get(v);
                if (idx !== undefined && (bestIndex === -1 || idx < bestIndex)) {
                    bestIndex = idx;
                }
            }
            return [r.resourceId, bestIndex];
        })
    );

    return [...filtered].sort((a, b) => {
        const aIndex = sortIndexMap.get(a.resourceId) ?? -1;
        const bIndex = sortIndexMap.get(b.resourceId) ?? -1;

        // Resources not found in active order go to the end
        if (aIndex === -1 && bIndex === -1) return 0;
        if (aIndex === -1) return 1;
        if (bIndex === -1) return -1;

        return aIndex - bIndex;
    });
};
